import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  HiOutlinePaperClip,
  HiOutlineEye,
  HiOutlineTrash,
  HiOutlineDocument,
} from "react-icons/hi";
import FileDropzone from "../../../atoms/FileDropzone";
import { CustomButton } from "../../../atoms/CustomButton";
import { usePrivateUploadFile } from "../../../../hooks/cdn";
import {
  useGetTaskAttachment,
  useDeleteTaskAttachment,
} from "../../../../hooks/todos";
import {
  TodoAttachment,
  MAX_TODO_ATTACHMENTS,
  MAX_TODO_ATTACHMENT_SIZE_MB,
} from "../../../../types/TodoTypes";

interface Props {
  customerId: string;
  entityId: string;
  boardId: string;
  taskId: string;
  attachments: TodoAttachment[];
  onChange: (attachments: TodoAttachment[]) => void;
  disabled?: boolean;
  canDelete?: boolean;
}

const TaskAttachments: React.FC<Props> = ({
  customerId,
  entityId,
  boardId,
  taskId,
  attachments,
  onChange,
  disabled = false,
  canDelete = true,
}) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [busyFile, setBusyFile] = useState<string | null>(null);

  const { mutate: uploadPrivate, isLoading: isUploading } =
    usePrivateUploadFile();
  const { mutate: getTaskAttachment } = useGetTaskAttachment(
    customerId,
    entityId
  );
  const { mutate: deleteTaskAttachment } = useDeleteTaskAttachment(
    customerId,
    entityId
  );

  const atCap = attachments.length >= MAX_TODO_ATTACHMENTS;

  const handleUpload = (file: File) => {
    if (atCap) return;
    if (file.size > MAX_TODO_ATTACHMENT_SIZE_MB * 1024 * 1024) {
      toast.error(
        t("todos.task.attachments.sizeError", {
          max: MAX_TODO_ATTACHMENT_SIZE_MB,
        }) as string
      );
      setSelectedFile(null);
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    uploadPrivate(
      {
        user_identification: customerId,
        entity_identification: entityId,
        data: formData,
      },
      {
        onSuccess: (res) => {
          const element = res?.data?.element?.[0];
          if (!element?.file) {
            toast.error(t("todos.task.attachments.uploadError") as string);
            return;
          }
          const next: TodoAttachment = {
            file: element.file,
            extension: element.extension || file.name.split(".").pop() || "",
            name: file.name,
          };
          onChange([...attachments, next]);
          setSelectedFile(null);
        },
      }
    );
  };

  const handleView = (att: TodoAttachment) => {
    const win = window.open("", "_blank");
    if (win) win.opener = null;
    getTaskAttachment(
      { boardId, taskId, file: att.file },
      {
        onSuccess: (res) => {
          const url = res?.data?.element?.url;
          if (url && win) win.location.href = url;
          else {
            win?.close();
            toast.error(t("todos.task.attachments.viewError") as string);
          }
        },
        onError: () => {
          win?.close();
          toast.error(t("todos.task.attachments.viewError") as string);
        },
      }
    );
  };

  const handleDelete = (att: TodoAttachment) => {
    setBusyFile(att.file);
    deleteTaskAttachment(
      { boardId, taskId, file: att.file },
      {
        onSuccess: () =>
          onChange(attachments.filter((a) => a.file !== att.file)),
        onError: () => {
          toast.error(t("todos.task.attachments.deleteError") as string);
        },
        onSettled: () => setBusyFile(null),
      }
    );
  };

  return (
    <div className="space-y-2">
      {attachments.length > 0 && (
        <ul className="space-y-1.5">
          {attachments.map((att) => (
            <li
              key={att.file}
              className="flex items-center gap-2.5 rounded-lg border border-gray-100 bg-white px-3 py-2"
            >
              <div className="w-8 h-8 rounded bg-gray-50 flex items-center justify-center shrink-0">
                <HiOutlineDocument size={18} className="text-gray-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="text-sm text-gray-800 truncate"
                  title={att.name}
                >
                  {att.name}
                </div>
                {att.extension && (
                  <div className="text-xs text-gray-400 uppercase">
                    {att.extension}
                  </div>
                )}
              </div>
              <CustomButton
                type="button"
                variant="none"
                size="icon"
                onClick={() => handleView(att)}
                className="shrink-0 rounded-md text-gray-400 enabled:hover:text-blue-600"
                icon={<HiOutlineEye size={18} />}
                aria-label={t("todos.task.attachments.view") as string}
              />
              {!disabled && canDelete && (
                <CustomButton
                  type="button"
                  variant="none"
                  size="icon"
                  onClick={() => handleDelete(att)}
                  disabled={busyFile === att.file}
                  className="shrink-0 rounded-md text-gray-400 enabled:hover:text-red-600"
                  icon={<HiOutlineTrash size={18} />}
                  aria-label={t("todos.task.attachments.delete") as string}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {!disabled && !atCap && (
        <FileDropzone
          selectedFile={selectedFile}
          onFileSelected={(file) => {
            setSelectedFile(file);
            handleUpload(file);
          }}
          onFileRemoved={() => setSelectedFile(null)}
          maxSizeMB={MAX_TODO_ATTACHMENT_SIZE_MB}
          disabled={isUploading}
          hint={
            t("todos.task.attachments.hint", {
              max: MAX_TODO_ATTACHMENT_SIZE_MB,
            }) as string
          }
        />
      )}
      {isUploading && (
        <p className="text-xs text-gray-400">
          {t("todos.task.attachments.uploading")}
        </p>
      )}
      {atCap && (
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <HiOutlinePaperClip size={13} />
          {t("todos.task.attachments.capReached", {
            max: MAX_TODO_ATTACHMENTS,
          })}
        </p>
      )}
    </div>
  );
};

export default TaskAttachments;
